import React from 'react';
import base64Changelog from './base64-transformer.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeError } from '@common/hooks/useNodeError';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

import {
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeOutput,
  NodeType,
  NodeInputType,
  Base64TransformerNodeState,
} from '../..';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Base64 Transformer',
  identifier: 'Base64Transformer',
  command_id: 'utility.base64',
  icon: 'base64_transformer',
  background: '#F35C5C',
  changelog: base64Changelog,
  anchors: [
    {
      label: 'Execute',
      id: 'utility.base64.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'utility.base64.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'Finished',
      id: 'utility.base64.stored',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Output',
      id: 'utility.base64.output',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
};

export const state: Base64TransformerNodeState = {
  operation: 'encode',
  input: {
    type: NodeInputType.Expression,
    value: '',
  },
};

export const fields: NodeField[] = [
  {
    id: 'operation',
    tab: '1',
    type: NodeFieldType.Toggle,
    options: [
      { value: 'encode', label: 'Encode' },
      { value: 'decode', label: 'Decode' },
    ],
    label: 'Operation Type',
    description:
      'Select the operation type that you would like to perform. Encoding will convert a string to base64, while decoding will convert a base64 string back to a normal string.',
  },
  {
    id: 'input',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Input String',
    description:
      "The string that you'd like to encode or decode. This can be a string or an expression that evaluates to a string.",
    placeholder: 'Enter a string to encode or decode',
  },
];

export const output: NodeOutput = {
  default: {
    utility: {
      base64: {
        decoded: 'string',
        encoded: 'string',
      },
    },
  },
  dynamic: null,
};

export const Base64Transformer: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'utility.base64') return null;

  const operationType = command?.state?.operation;
  const displayTitle = operationType ? `Base64 ${operationType === 'encode' ? '(Encode)' : '(Decode)'}` : title;
  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={displayTitle} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
